/**
 * Admin Audit Log Page
 * Track all admin actions for compliance and debugging
 */

import { useState } from 'react';
import { Search, Filter, Copy } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

interface AuditLog {
  id: string;
  timestamp: string;
  admin: string;
  action: string;
  type: 'create' | 'update' | 'delete' | 'suspend' | 'credit_adjust' | 'login' | 'export';
  target: string;
  targetId: string;
  details: string;
  status: 'success' | 'failed';
}

export default function AdminAuditLog() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'create' | 'update' | 'delete' | 'suspend' | 'credit_adjust' | 'login' | 'export'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'success' | 'failed'>('all');
  const [sortBy, setSortBy] = useState<'timestamp' | 'admin' | 'action'>('timestamp');
  const [showFilters, setShowFilters] = useState(false);

  // Mock data - TODO: Replace with actual API call
  const [logs] = useState<AuditLog[]>([
    {
      id: 'LOG-001',
      timestamp: '2024-11-01 19:29:45',
      admin: 'admin@playxworld.com',
      action: 'Created new game',
      type: 'create',
      target: 'Game',
      targetId: 'GAME-123',
      details: 'Created game "Valorant Promotion" with 12 packages',
      status: 'success',
    },
    {
      id: 'LOG-002',
      timestamp: '2024-11-01 18:45:30',
      admin: 'admin@playxworld.com',
      action: 'Updated game pricing',
      type: 'update',
      target: 'Game',
      targetId: 'GAME-456',
      details: 'Updated pricing for League of Legends packages',
      status: 'success',
    },
    {
      id: 'LOG-003',
      timestamp: '2024-11-01 17:15:20',
      admin: 'admin@playxworld.com',
      action: 'Adjusted user credit',
      type: 'credit_adjust',
      target: 'User',
      targetId: 'user_123',
      details: 'Added 1000 credit for refund (Reason: Failed transaction)',
      status: 'success',
    },
    {
      id: 'LOG-004',
      timestamp: '2024-11-01 16:30:10',
      admin: 'admin@playxworld.com',
      action: 'Suspended user',
      type: 'suspend',
      target: 'User',
      targetId: 'user_789',
      details: 'Suspended user for suspicious activity',
      status: 'success',
    },
    {
      id: 'LOG-005',
      timestamp: '2024-11-01 15:45:00',
      admin: 'admin@playxworld.com',
      action: 'Exported transactions',
      type: 'export',
      target: 'Report',
      targetId: 'REPORT-001',
      details: 'Exported monthly transaction report',
      status: 'success',
    },
  ]);

  // Filter logs
  const filteredLogs = logs
    .filter((log) => {
      if (searchQuery && !log.admin.toLowerCase().includes(searchQuery.toLowerCase()) &&
          !log.action.toLowerCase().includes(searchQuery.toLowerCase()) &&
          !log.targetId.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      if (filterType !== 'all' && log.type !== filterType) {
        return false;
      }
      if (filterStatus !== 'all' && log.status !== filterStatus) {
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'timestamp') return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      if (sortBy === 'admin') return a.admin.localeCompare(b.admin);
      return a.action.localeCompare(b.action);
    });

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'create':
        return 'bg-green-100 text-green-800';
      case 'update':
        return 'bg-blue-100 text-blue-800';
      case 'delete':
        return 'bg-red-100 text-red-800';
      case 'suspend':
        return 'bg-orange-100 text-orange-800';
      case 'credit_adjust':
        return 'bg-purple-100 text-purple-800';
      case 'login':
        return 'bg-cyan-100 text-cyan-800';
      case 'export':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    return status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  const handleCopyId = (id: string) => {
    navigator.clipboard.writeText(id);
    toast.success('Copied to clipboard');
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-black">Audit Log</h1>
            <p className="text-black/60 text-sm mt-1">
              Track all admin actions and system events
            </p>
          </div>
          <Button onClick={() => setShowFilters(!showFilters)} variant="outline" size="sm">
            <Filter className="w-4 h-4 mr-2" />
            Filters
          </Button>
        </div>

        {/* Filters */}
        {showFilters && (
          <Card className="backoffice-card">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>

                <Select value={filterType} onValueChange={(v) => setFilterType(v as any)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Action Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="create">Create</SelectItem>
                    <SelectItem value="update">Update</SelectItem>
                    <SelectItem value="delete">Delete</SelectItem>
                    <SelectItem value="suspend">Suspend</SelectItem>
                    <SelectItem value="credit_adjust">Credit Adjust</SelectItem>
                    <SelectItem value="login">Login</SelectItem>
                    <SelectItem value="export">Export</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as any)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="success">Success</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={sortBy} onValueChange={(v) => setSortBy(v as any)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="timestamp">Timestamp</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="action">Action</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Audit Logs */}
        <Card className="backoffice-card">
          <CardHeader className="backoffice-card-header">
            <CardTitle className="text-black">Activity Log</CardTitle>
            <CardDescription className="text-black/60">Total: {filteredLogs.length} events</CardDescription>
          </CardHeader>
          <CardContent>
            {filteredLogs.length > 0 ? (
              <div className="space-y-4">
                {filteredLogs.map((log) => (
                  <div key={log.id} className="border-2 border-black rounded-lg p-4 hover:bg-gray-100 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-3 flex-wrap">
                          <Badge className={getTypeColor(log.type)}>
                            {log.type.replace('_', ' ').toUpperCase()}
                          </Badge>
                          <Badge className={getStatusColor(log.status)}>
                            {log.status.toUpperCase()}
                          </Badge>
                          <span className="text-sm text-black">{log.timestamp}</span>
                        </div>
                        <div>
                          <p className="font-medium text-black">{log.action}</p>
                          <p className="text-sm text-black/60">{log.details}</p>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-black/60">
                          <span>Admin: {log.admin}</span>
                          <span>•</span>
                          <span>Target: {log.target}</span>
                          <span>•</span>
                          <span className="font-mono">{log.targetId}</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleCopyId(log.targetId)}
                            className="h-6 w-6 p-0 ml-1"
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-black/60">
                No audit logs found
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
